import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import useOperationalHealth from '../../hooks/useOperationalHealth';
import { startAllMonitors, createMissingMonitors } from '../../services/MonitorsService';
import { activateByActionType } from '../../services/AutomationsService';

/**
 * Banner contextual de saúde operacional para páginas de Monitores e Automações.
 *
 * props:
 * - filterIds: string[] — quais issue.id exibir nesta página
 * - title: string — título do banner (default "Atenção")
 */
function SectionHealthBanner({ filterIds, title = 'Atenção' }) {
    const { health, loading, refresh } = useOperationalHealth();
    const [working, setWorking] = useState(null); // id da ação em andamento
    const [feedback, setFeedback] = useState(null); // { ok, message }
    const history = useHistory();

    if (loading || !health) return null;

    const issues = (health.issues || []).filter(i => filterIds.includes(i.id));
    if (!issues.length) return null;

    async function run(id, fn) {
        setWorking(id);
        setFeedback(null);
        try {
            const r = await fn();
            setFeedback({ ok: true, message: r.message || r.activated != null
                ? (r.activated != null ? `${r.activated} automação(ões) ativada(s).` : r.message)
                : (r.started != null ? `${r.started} monitor(es) iniciado(s).` : 'Concluído.') });
            setTimeout(refresh, 1200);
        } catch (err) {
            setFeedback({ ok: false, message: err.response?.data?.error || err.message || 'Erro.' });
        } finally {
            setWorking(null);
        }
    }

    function actionFor(issue) {
        switch (issue.id) {
            case 'scanner_stopped':
            case 'regime_stopped':
            case 'candles_stopped':
                return { label: '▶ Iniciar básicos', fn: () => run(issue.id, () => startAllMonitors('basic')) };
            case 'no_scanner':
            case 'no_regime':
            case 'no_candles':
                return { label: '➕ Criar monitores', fn: () => run(issue.id, () => createMissingMonitors()) };
            case 'ai_scan_inactive':
                return { label: '✓ Ativar scanner', fn: () => run(issue.id, () => activateByActionType('AI_MARKET_SCAN')) };
            case 'no_ai_scan':
                return { label: '📋 Templates', fn: () => history.push('/strategies') };
            case 'no_strategy':
            case 'no_active_strategy':
                return { label: '📋 Templates', fn: () => history.push('/strategies') };
            default:
                return { label: issue.actionLabel || 'Ver', fn: () => history.push(issue.action) };
        }
    }

    const isRed = issues.some(i => i.severity === 'red');

    return (
        <div className={`alert ${isRed ? 'alert-danger' : 'alert-warning'} py-2 mb-3`} style={{ fontSize: 13 }}>
            <strong className='d-block mb-1'>{isRed ? '🔴' : '🟡'} {title}</strong>
            {feedback && (
                <div className={`alert ${feedback.ok ? 'alert-success' : 'alert-danger'} py-1 mb-2`} style={{ fontSize: 12 }}>
                    {feedback.message}
                </div>
            )}
            <div className='d-flex flex-column gap-1'>
                {issues.map(issue => {
                    const action = actionFor(issue);
                    return (
                        <div key={issue.id} className='d-flex align-items-center justify-content-between gap-2'>
                            <span>{issue.text}</span>
                            <button
                                className='btn btn-sm btn-outline-dark py-0 px-2 flex-shrink-0'
                                style={{ fontSize: 11, whiteSpace: 'nowrap' }}
                                disabled={working === issue.id}
                                onClick={action.fn}>
                                {working === issue.id ? '...' : action.label}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default SectionHealthBanner;
